/**
 * Resources Listing — front-end filtering.
 *
 * Progressive enhancement over the server-rendered <form method="get">:
 * checking a Type/Topic box, typing in Search, or changing Sort filters the
 * cards live via the /accuity/v1/resources REST route instead of reloading.
 * Page-number links navigate between pages in place; the URL is kept in sync
 * so any filtered + paged view stays shareable. With JS disabled the form
 * simply submits as a GET and the pagination links are real anchors.
 */

import { __, _n, sprintf } from '@wordpress/i18n';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Read the current filter state from a block's form controls.
 *
 * @param {Object} els Cached element references for one block.
 * @return {{type:string[],topic:string[],search:string,orderby:string}} Filter state.
 */
function collectFilters( els ) {
	const checked = ( name ) =>
		Array.from(
			els.form.querySelectorAll(
				`input[name="${ name }[]"]:checked`
			)
		).map( ( input ) => input.value );

	return {
		type: checked( 'type' ),
		topic: checked( 'topic' ),
		search: els.search ? els.search.value.trim() : '',
		orderby: els.sort ? els.sort.value : 'newest',
	};
}

/**
 * Build the REST query string for a filter state and page.
 *
 * @param {Object} filters Filter state from collectFilters().
 * @param {number} page    1-based page number.
 * @return {URLSearchParams} Query params.
 */
function buildParams( filters, page ) {
	const params = new URLSearchParams();
	if ( filters.type.length ) {
		params.set( 'type', filters.type.join( ',' ) );
	}
	if ( filters.topic.length ) {
		params.set( 'topic', filters.topic.join( ',' ) );
	}
	if ( filters.search ) {
		params.set( 'search', filters.search );
	}
	if ( filters.orderby && filters.orderby !== 'newest' ) {
		params.set( 'orderby', filters.orderby );
	}
	if ( page > 1 ) {
		params.set( 'paged', String( page ) );
	}
	return params;
}

/**
 * Reflect the active filters + page in the browser address bar so the
 * current view can be bookmarked or shared.
 *
 * @param {Object} filters Filter state.
 * @param {number} page    Current page.
 */
function syncUrl( filters, page ) {
	const params = buildParams( filters, page );
	const query = params.toString();
	const url = query
		? `${ window.location.pathname }?${ query }`
		: window.location.pathname;
	window.history.replaceState( null, '', url );
}

/**
 * Replace the native <select> sort control with a custom listbox dropdown.
 *
 * @param {Object}   els      Cached element references for one block.
 * @param {Function} onChange Called after the visitor picks a new sort option.
 * @return {Function|null} A fn that re-syncs the custom UI from the <select>,
 *                         or null when the custom dropdown markup is absent.
 */
function initSortDropdown( els, onChange ) {
	const { sort, sortToggle, sortMenu, sortValue } = els;
	if ( ! sort || ! sortToggle || ! sortMenu ) {
		return null;
	}
	const options = Array.from(
		sortMenu.querySelectorAll( '.resources-listing__sort-option' )
	);
	if ( ! options.length ) {
		return null;
	}

	sort.hidden = true;
	sortToggle.hidden = false;

	let isOpen = false;

	// Options the listbox should skip while one or more are toggled hidden
	// (e.g. "Most Relevant" before a search has been entered).
	const visibleOptions = () => options.filter( ( o ) => ! o.hidden );

	const setOpen = ( open ) => {
		isOpen = open;
		sortMenu.hidden = ! open;
		sortToggle.setAttribute( 'aria-expanded', String( open ) );
		if ( open ) {
			const vis = visibleOptions();
			const selected =
				vis.find(
					( option ) =>
						option.getAttribute( 'aria-selected' ) === 'true'
				) || vis[ 0 ];
			if ( selected ) {
				selected.focus();
			}
		}
	};

	const syncFromSelect = () => {
		const match =
			options.find( ( option ) => option.dataset.value === sort.value ) ||
			options[ 0 ];
		options.forEach( ( option ) =>
			option.setAttribute(
				'aria-selected',
				String( option === match )
			)
		);
		if ( sortValue ) {
			sortValue.textContent = match.textContent.trim();
		}
	};

	const choose = ( option ) => {
		sort.value = option.dataset.value;
		syncFromSelect();
		setOpen( false );
		sortToggle.focus();
		onChange();
	};

	// Move focus to the next/previous *visible* option.
	const focusByOffset = ( current, delta ) => {
		const vis = visibleOptions();
		const i = vis.indexOf( current );
		const target = vis[ Math.min( Math.max( i + delta, 0 ), vis.length - 1 ) ];
		if ( target ) {
			target.focus();
		}
	};

	sortToggle.addEventListener( 'click', () => setOpen( ! isOpen ) );

	sortToggle.addEventListener( 'keydown', ( event ) => {
		if (
			[ 'ArrowDown', 'ArrowUp', 'Enter', ' ' ].indexOf( event.key ) !== -1
		) {
			event.preventDefault();
			setOpen( true );
		}
	} );

	options.forEach( ( option ) => {
		option.addEventListener( 'click', () => choose( option ) );
		option.addEventListener( 'keydown', ( event ) => {
			switch ( event.key ) {
				case 'ArrowDown':
					event.preventDefault();
					focusByOffset( option, 1 );
					break;
				case 'ArrowUp':
					event.preventDefault();
					focusByOffset( option, -1 );
					break;
				case 'Home': {
					event.preventDefault();
					const vis = visibleOptions();
					( vis[ 0 ] || option ).focus();
					break;
				}
				case 'End': {
					event.preventDefault();
					const vis = visibleOptions();
					( vis[ vis.length - 1 ] || option ).focus();
					break;
				}
				case 'Enter':
				case ' ':
					event.preventDefault();
					choose( option );
					break;
				case 'Escape':
					event.preventDefault();
					setOpen( false );
					sortToggle.focus();
					break;
				case 'Tab':
					setOpen( false );
					break;
				default:
					break;
			}
		} );
	} );

	document.addEventListener( 'click', ( event ) => {
		if (
			isOpen &&
			sortToggle.parentElement &&
			! sortToggle.parentElement.contains( event.target )
		) {
			setOpen( false );
		}
	} );

	// Search-only options (e.g. "Most Relevant") are revealed only once the
	// visitor has entered a search term. Toggling them off resets the active
	// sort to Newest so the control never sits on a hidden value.
	const searchOnlyValues = options
		.filter( ( o ) =>
			o.classList.contains( 'resources-listing__sort-option--search-only' )
		)
		.map( ( o ) => o.dataset.value );

	const setSearchOnlyVisible = ( visible ) => {
		if ( ! searchOnlyValues.length ) {
			return;
		}
		options.forEach( ( o ) => {
			if ( searchOnlyValues.indexOf( o.dataset.value ) !== -1 ) {
				o.hidden = ! visible;
			}
		} );
		Array.from( sort.options ).forEach( ( o ) => {
			if ( o.dataset.searchOnly === '1' ) {
				o.hidden = ! visible;
				o.disabled = ! visible;
			}
		} );
		if ( ! visible && searchOnlyValues.indexOf( sort.value ) !== -1 ) {
			sort.value = 'newest';
		}
		syncFromSelect();
	};

	return { syncFromSelect, setSearchOnlyVisible };
}

/**
 * Replace one filter group's checkboxes with the server's "options still
 * relevant for the visitor's current selection" list — kinds narrowed by the
 * Topic selection, topics narrowed by the Type selection.
 *
 * Contract: the server always includes the visitor's currently checked
 * options in the list (see accuity_resources_relevant_kinds() /
 * accuity_resources_relevant_topics()), so rebuilding from `list` can never
 * drop a checked box the visitor would have no way to untick.
 *
 * @param {Object}                          els   Cached element references for one block.
 * @param {string}                          group Filter group key — 'type' or 'topic'.
 * @param {Array<{key:string,label:string}>} list  Option list returned by REST.
 */
function rebuildFilterGroup( els, group, list ) {
	if ( ! Array.isArray( list ) ) {
		return;
	}
	const fieldset = els.form.querySelector(
		`.resources-listing__filter-group[data-filter-group="${ group }"]`
	);
	if ( ! fieldset ) {
		return;
	}
	const ul = fieldset.querySelector( '.resources-listing__filter-list' );
	if ( ! ul ) {
		return;
	}

	const checked = new Set(
		Array.from(
			ul.querySelectorAll( `input[name="${ group }[]"]:checked` )
		).map( ( input ) => input.value )
	);

	fieldset.hidden = list.length === 0;
	if ( ! list.length ) {
		ul.replaceChildren();
		return;
	}

	const fragment = document.createDocumentFragment();
	list.forEach( ( option ) => {
		const li = document.createElement( 'li' );
		li.className = 'resources-listing__filter-item';

		const label = document.createElement( 'label' );
		label.className = 'resources-listing__checkbox';

		const input = document.createElement( 'input' );
		input.type = 'checkbox';
		input.className = 'resources-listing__checkbox-input';
		input.name = `${ group }[]`;
		input.value = option.key;
		if ( checked.has( option.key ) ) {
			input.checked = true;
		}

		const span = document.createElement( 'span' );
		span.className = 'resources-listing__checkbox-label';
		span.textContent = option.label;

		label.append( input, span );
		li.append( label );
		fragment.append( li );
	} );

	ul.replaceChildren( fragment );
}

/**
 * Build the windowed page list shown in the pagination nav.
 *
 * Same shape as the PHP server render: show every page up to the visible cap,
 * and only collapse the middle with a gap once there are more pages than that.
 * `null` entries are gap placeholders ("…").
 *
 * @param {number} current  Current page (1-based).
 * @param {number} maxPages Total page count.
 * @return {Array<number|null>} Sequence of page numbers with null gaps.
 */
function buildPageWindow( current, maxPages ) {
	const maxVisible = 6;
	const pages = new Set();
	const add = ( n ) => {
		if ( n >= 1 && n <= maxPages ) {
			pages.add( n );
		}
	};

	if ( maxPages <= maxVisible ) {
		for ( let i = 1; i <= maxPages; i++ ) {
			add( i );
		}
	} else {
		add( 1 );
		add( maxPages );

		const middleSlots = maxVisible - 2;
		let start = current - Math.floor( ( middleSlots - 1 ) / 2 );
		let end = current + Math.ceil( ( middleSlots - 1 ) / 2 );

		if ( start < 2 ) {
			end += 2 - start;
			start = 2;
		}
		if ( end > maxPages - 1 ) {
			start -= end - ( maxPages - 1 );
			end = maxPages - 1;
		}
		start = Math.max( 2, start );

		for ( let i = start; i <= end; i++ ) {
			add( i );
		}
	}

	const sorted = Array.from( pages ).sort( ( a, b ) => a - b );
	const out = [];
	let prev = 0;
	sorted.forEach( ( page ) => {
		if ( prev && page - prev > 1 ) {
			out.push( null );
		}
		out.push( page );
		prev = page;
	} );
	return out;
}

/**
 * Build the inline SVG arrow used by the prev / next pagination buttons.
 *
 * @return {SVGElement} The arrow SVG node.
 */
function buildArrowSvg() {
	const svg = document.createElementNS(
		'http://www.w3.org/2000/svg',
		'svg'
	);
	svg.setAttribute(
		'class',
		'resources-listing__pagination-arrow-svg'
	);
	svg.setAttribute( 'width', '17' );
	svg.setAttribute( 'height', '15' );
	svg.setAttribute( 'viewBox', '0 0 17 15' );
	svg.setAttribute( 'fill', 'none' );
	svg.setAttribute( 'aria-hidden', 'true' );
	svg.setAttribute( 'focusable', 'false' );
	const path = document.createElementNS(
		'http://www.w3.org/2000/svg',
		'path'
	);
	path.setAttribute(
		'd',
		'M1 6.36377C0.447715 6.36377 4.82823e-08 6.81148 0 7.36377C-4.82823e-08 7.91605 0.447715 8.36377 1 8.36377L1 7.36377L1 6.36377ZM16.4212 8.07088C16.8117 7.68035 16.8117 7.04719 16.4212 6.65666L10.0572 0.292703C9.66668 -0.0978219 9.03351 -0.097822 8.64299 0.292702C8.25246 0.683227 8.25246 1.31639 8.64299 1.70692L14.2998 7.36377L8.64299 13.0206C8.25246 13.4111 8.25246 14.0443 8.64299 14.4351C9.03351 14.8254 9.66668 14.8254 10.0572 14.4351L16.4212 8.07088ZM1 7.36377L1 8.36377L15.7141 8.36377L15.7141 7.36377L15.7141 6.36377L1 6.36377L1 7.36377Z'
	);
	path.setAttribute( 'fill', 'currentColor' );
	svg.append( path );
	return svg;
}

/**
 * Format a page number with a 2-digit zero pad ("01", "02", ..., "10").
 *
 * @param {number} n Page number.
 * @return {string}
 */
function padPage( n ) {
	return n < 10 ? '0' + n : String( n );
}

/**
 * Repaint the pagination nav from the current state.
 *
 * Built via the DOM API (not innerHTML) so labels can't be injected.
 *
 * @param {Object} els        Cached element references for one block.
 * @param {Object} filters    Filter state — used to build link hrefs.
 * @param {number} current    Current page (1-based).
 * @param {number} maxPages   Total page count.
 * @param {number} foundPosts Total matching posts (across all pages).
 */
function renderPagination( els, filters, current, maxPages, foundPosts ) {
	if ( ! els.pagination ) {
		return;
	}
	if ( foundPosts <= 0 ) {
		els.pagination.hidden = true;
		els.pagination.replaceChildren();
		return;
	}
	els.pagination.hidden = false;

	const perPage =
		parseInt( els.block.dataset.perPage, 10 ) || foundPosts;
	const rangeStart = ( current - 1 ) * perPage + 1;
	const rangeEnd = Math.min( current * perPage, foundPosts );

	const showing = document.createElement( 'p' );
	showing.className = 'resources-listing__pagination-showing';
	showing.textContent = sprintf(
		/* translators: 1: first item on page, 2: last item on page, 3: total items. */
		__( 'Showing %1$s–%2$s of %3$s', 'accuity' ),
		rangeStart.toLocaleString(),
		rangeEnd.toLocaleString(),
		foundPosts.toLocaleString()
	);

	const children = [ showing ];

	if ( maxPages > 1 ) {
		const ul = document.createElement( 'ul' );
		ul.className = 'resources-listing__pagination-list';

		const hrefFor = ( page ) => {
			const params = buildParams( filters, page );
			const query = params.toString();
			return query
				? `${ window.location.pathname }?${ query }`
				: window.location.pathname;
		};

		const appendArrow = ( page, modifier, ariaLabel, rel ) => {
			const li = document.createElement( 'li' );
			li.className = 'resources-listing__pagination-item';
			const a = document.createElement( 'a' );
			a.className =
				'resources-listing__pagination-arrow resources-listing__pagination-arrow--' +
				modifier;
			a.href = hrefFor( page );
			a.dataset.resourcesPage = String( page );
			a.setAttribute( 'aria-label', ariaLabel );
			a.setAttribute( 'rel', rel );
			a.append( buildArrowSvg() );
			li.append( a );
			ul.append( li );
		};

		const appendNumberLink = ( page ) => {
			const li = document.createElement( 'li' );
			li.className = 'resources-listing__pagination-item';
			const a = document.createElement( 'a' );
			a.className = 'resources-listing__pagination-link';
			a.href = hrefFor( page );
			a.dataset.resourcesPage = String( page );
			a.setAttribute(
				'aria-label',
				sprintf(
					/* translators: %d: page number. */
					__( 'Go to page %d', 'accuity' ),
					page
				)
			);
			a.textContent = padPage( page );
			li.append( a );
			ul.append( li );
		};

		if ( current > 1 ) {
			appendArrow(
				current - 1,
				'prev',
				__( 'Previous page', 'accuity' ),
				'prev'
			);
		}

		buildPageWindow( current, maxPages ).forEach( ( page ) => {
			if ( page === null ) {
				const li = document.createElement( 'li' );
				li.className =
					'resources-listing__pagination-item resources-listing__pagination-item--gap';
				li.setAttribute( 'aria-hidden', 'true' );
				li.textContent = '…';
				ul.append( li );
				return;
			}
			if ( page === current ) {
				const li = document.createElement( 'li' );
				li.className = 'resources-listing__pagination-item';
				const span = document.createElement( 'span' );
				span.className = 'resources-listing__pagination-current';
				span.setAttribute( 'aria-current', 'page' );
				span.textContent = padPage( page );
				li.append( span );
				ul.append( li );
				return;
			}
			appendNumberLink( page );
		} );

		if ( current < maxPages ) {
			appendArrow(
				current + 1,
				'next',
				__( 'Next page', 'accuity' ),
				'next'
			);
		}

		children.push( ul );
	}

	els.pagination.replaceChildren( ...children );
}

/**
 * Wire up a single resources-listing block instance.
 *
 * @param {HTMLElement} block The .resources-listing root element.
 */
function initBlock( block ) {
	const els = {
		block,
		form: block.querySelector( '.resources-listing__form' ),
		search: block.querySelector( '.resources-listing__search-input' ),
		sort: block.querySelector( '.resources-listing__sort-select' ),
		sortValue: block.querySelector( '.resources-listing__sort-value-text' ),
		sortToggle: block.querySelector( '.resources-listing__sort-toggle' ),
		sortMenu: block.querySelector( '.resources-listing__sort-menu' ),
		sidebar: block.querySelector( '.resources-listing__sidebar' ),
		toggle: block.querySelector( '.resources-listing__filter-toggle' ),
		clear: block.querySelector( '.resources-listing__clear' ),
		cards: block.querySelector( '.resources-listing__cards' ),
		empty: block.querySelector( '.resources-listing__empty' ),
		status: block.querySelector( '.resources-listing__status' ),
		pagination: block.querySelector( '.resources-listing__pagination' ),
	};

	const restUrl = block.dataset.restUrl;
	if ( ! els.form || ! els.cards || ! restUrl ) {
		return;
	}

	let currentPage = parseInt( block.dataset.currentPage, 10 ) || 1;
	let maxPages = parseInt( block.dataset.maxPages, 10 ) || 1;
	let controller = null;

	if ( els.sidebar ) {
		els.sidebar.classList.add( 'js-filters' );
	}

	/**
	 * Fetch a page of results.
	 *
	 * @param {Object} filters       Filter state.
	 * @param {number} page          Page to request.
	 * @param {Object} options       Extra knobs.
	 * @param {boolean} options.scrollIntoView Scroll to the top of the cards on success.
	 */
	const fetchResults = ( filters, page, options = {} ) => {
		if ( controller ) {
			controller.abort();
		}
		controller = new AbortController();

		block.classList.add( 'is-loading' );
		els.cards.setAttribute( 'aria-busy', 'true' );

		const params = buildParams( filters, page );

		// Curation config (manual pins + promoted event) travels on the AJAX
		// request only — not the shareable URL (syncUrl uses buildParams) — so
		// live results match the initial server render. The server ignores it
		// on any filtered/searched/re-sorted view.
		if ( block.dataset.pinned ) {
			params.set( 'pinned', block.dataset.pinned );
		}
		if ( block.dataset.promoteEvent === '1' ) {
			params.set( 'promoteEvent', '1' );
		}
		if ( block.dataset.promotePosition ) {
			params.set( 'promotePosition', block.dataset.promotePosition );
		}

		const sep = restUrl.indexOf( '?' ) === -1 ? '?' : '&';

		fetch( `${ restUrl }${ sep }${ params.toString() }`, {
			signal: controller.signal,
			headers: { Accept: 'application/json' },
		} )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw new Error( `HTTP ${ response.status }` );
				}
				return response.json();
			} )
			.then( ( data ) => {
				currentPage = data.page || page;
				maxPages = data.maxPages || 1;

				els.cards.innerHTML = data.html || '';
				rebuildFilterGroup( els, 'type', data.relevantKinds );
				rebuildFilterGroup( els, 'topic', data.relevantTopics );

				const hasResults = els.cards.children.length > 0;
				els.cards.hidden = ! hasResults;
				if ( els.empty ) {
					els.empty.hidden = hasResults;
				}
				if ( els.status ) {
					const count = data.found || 0;
					els.status.textContent = sprintf(
						/* translators: %d: number of matching resources. */
						_n(
							'%d resource found',
							'%d resources found',
							count,
							'accuity'
						),
						count
					);
				}
				renderPagination(
					els,
					filters,
					currentPage,
					maxPages,
					data.found || 0
				);
				syncUrl( filters, currentPage );

				if ( options.scrollIntoView && els.cards.scrollIntoView ) {
					els.cards.scrollIntoView( {
						behavior: 'smooth',
						block: 'start',
					} );
				}
			} )
			.catch( ( error ) => {
				if ( error.name === 'AbortError' ) {
					return;
				}
				if ( els.status ) {
					els.status.textContent = __(
						'Something went wrong loading resources. Please try again.',
						'accuity'
					);
				}
			} )
			.finally( () => {
				block.classList.remove( 'is-loading' );
				els.cards.removeAttribute( 'aria-busy' );
			} );
	};

	const applyFilters = () => {
		currentPage = 1;
		const filters = collectFilters( els );
		fetchResults( filters, 1 );
	};

	const sortApi = initSortDropdown( els, applyFilters );

	// ── Filter / sort changes ────────────────────────────────────────────────
	els.form.addEventListener( 'change', ( event ) => {
		if (
			event.target.matches(
				'.resources-listing__checkbox-input, .resources-listing__sort-select'
			)
		) {
			applyFilters();
		}
	} );

	// ── Search (debounced) ───────────────────────────────────────────────────
	if ( els.search ) {
		let searchTimer = null;
		els.search.addEventListener( 'input', () => {
			// Reveal/hide "Most Relevant" immediately as the visitor types
			// (the results fetch stays debounced below).
			if ( sortApi ) {
				sortApi.setSearchOnlyVisible( '' !== els.search.value.trim() );
			}
			window.clearTimeout( searchTimer );
			searchTimer = window.setTimeout(
				applyFilters,
				SEARCH_DEBOUNCE_MS
			);
		} );
	}

	// ── Enter key / no-op submit ─────────────────────────────────────────────
	els.form.addEventListener( 'submit', ( event ) => {
		event.preventDefault();
		applyFilters();
	} );

	// ── Clear All ────────────────────────────────────────────────────────────
	if ( els.clear ) {
		els.clear.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			els.form
				.querySelectorAll( '.resources-listing__checkbox-input:checked' )
				.forEach( ( input ) => {
					input.checked = false;
				} );
			if ( els.search ) {
				els.search.value = '';
			}
			if ( els.sort ) {
				els.sort.value = 'newest';
			}
			// Clearing search hides "Most Relevant" again (and resyncs the
			// custom dropdown label).
			if ( sortApi ) {
				sortApi.setSearchOnlyVisible( false );
			}
			applyFilters();
		} );
	}

	// ── Pagination ───────────────────────────────────────────────────────────
	if ( els.pagination ) {
		els.pagination.addEventListener( 'click', ( event ) => {
			const link = event.target.closest( '[data-resources-page]' );
			if ( ! link || ! els.pagination.contains( link ) ) {
				return;
			}
			const page = parseInt( link.dataset.resourcesPage, 10 );
			if ( ! page || page < 1 || page === currentPage ) {
				event.preventDefault();
				return;
			}
			event.preventDefault();
			fetchResults( collectFilters( els ), page, { scrollIntoView: true } );
		} );
	}

	// ── Mobile filter panel toggle ───────────────────────────────────────────
	if ( els.toggle && els.sidebar ) {
		els.toggle.addEventListener( 'click', () => {
			const open = els.sidebar.classList.toggle( 'is-open' );
			els.toggle.setAttribute( 'aria-expanded', String( open ) );
		} );
	}
}


document
	.querySelectorAll( '.resources-listing' )
	.forEach( ( block ) => initBlock( block ) );
